import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const FlipBookViewer = ({ publicationId, token }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const flipBookInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Check if we're in an iframe (embedded mode)
  useEffect(() => {
    setIsEmbedded(window.self !== window.top);
  }, []);

  // Fetch PDF URL from API
  const fetchPdfUrl = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const headers = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiUrl}/publications/${publicationId}/pdf`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch PDF URL');
      }

      const data = await response.json();
      return data.url;
    } catch (err) {
      console.error('Error fetching PDF URL:', err);
      setError('Failed to load publication. Please try again.');
      throw err;
    }
  };

  // Wait for libraries to load
  const waitForLibraries = () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)

      const checkLibraries = () => {
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.FlipBook) {
          console.log('✓ FlipBook library ready');
          resolve();
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            reject(new Error('FlipBook library failed to load. Please refresh the page.'));
          } else {
            setTimeout(checkLibraries, 100);
          }
        }
      };

      checkLibraries();
    });
  };

  // Initialize FlipBook
  const initializeFlipBook = async () => {
    try {
      setLoading(true);

      // Wait for libraries to be ready
      await waitForLibraries();

      // Fetch PDF URL
      const url = await fetchPdfUrl();

      const $ = window.jQuery;

      // Clear previous instance
      if (flipBookInstanceRef.current) {
        try {
          if (flipBookInstanceRef.current.dispose) {
            flipBookInstanceRef.current.dispose();
          }
        } catch (err) {
          console.warn('Error disposing previous flipbook:', err);
        }
        flipBookInstanceRef.current = null;
      }

      // Clear container
      if (containerRef.current) {
        $(containerRef.current).empty();
      }

      // Initialize FlipBook with a small delay to ensure DOM is ready
      setTimeout(() => {
        if (!containerRef.current) {
          setError('Container not available');
          return;
        }

        try {
          console.log('✓ FlipBook initializing (PDFJS_LOCALE configured globally)');

          // Use absolute URLs with origin for more reliable loading
          const baseUrl = window.location.origin;

          // Configuration for flipbook
          const flipBookConfig = {
            pdf: url,
            template: {
              html: `${baseUrl}/flipbook/templates/default-book-view.html`,
              styles: [`${baseUrl}/flipbook/css/short-white-book-view.css`],
              links: [
                {
                  rel: 'stylesheet',
                  href: `${baseUrl}/flipbook/css/font-awesome.min.css`,
                },
              ],
              script: `${baseUrl}/flipbook/js/default-book-view.js`,
            },
            controlsProps: {
              downloadURL: url,
              sound: false, // Disable sound globally
            },
          };

          // Hide controls in embed mode
          if (isEmbedded) {
            flipBookConfig.controlsProps.hide = true;
          }

          const flipBookInstance = $(containerRef.current).FlipBook(flipBookConfig);

          flipBookInstanceRef.current = flipBookInstance;
          setLoading(false);
          console.log('✓ FlipBook initialized successfully');
        } catch (err) {
          console.error('Error initializing FlipBook:', err);
          setError('Failed to initialize book viewer: ' + err.message);
          setLoading(false);
        }
      }, 100);
    } catch (err) {
      console.error('Error initializing FlipBook:', err);
      setError(err.message || 'Failed to initialize book viewer');
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeFlipBook();

    // Cleanup
    return () => {
      if (flipBookInstanceRef.current) {
        try {
          if (flipBookInstanceRef.current.dispose) {
            flipBookInstanceRef.current.dispose();
          }
        } catch (err) {
          console.warn('Error disposing flipbook on cleanup:', err);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationId, token, isEmbedded]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 text-xl mb-4">Error Loading Publication</div>
          <div className="text-gray-600 mb-6">{error}</div>
          <button
            onClick={() => navigate('/publications')}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Publications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flipbook-viewer bg-gray-900 min-h-screen relative">
      {/* Back Button - Only show if not embedded */}
      {/* {!isEmbedded && (
                <div className="absolute top-4 left-4 z-50">
                    <button
                        onClick={() => navigate('/publications')}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded shadow-lg transition flex items-center gap-2"
                        title="Back to Publications"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Back</span>
                    </button>
                </div>
            )} */}

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-40">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mb-4"></div>
          <div className="text-white text-lg">Loading publication...</div>
        </div>
      )}

      {/* FlipBook Container */}
      <div
        ref={containerRef}
        className="flipbook-container w-full h-screen"
        style={{ backgroundColor: '#333' }}
      />
    </div>
  );
};

export default FlipBookViewer;
